import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import type { Access, Ledger } from './api';
import { useLedgers } from './queries';

/**
 * Which ledger the app is currently looking at.
 *
 * `null` means "everything I can see" — a real, useful view for someone who
 * administers several accounts and wants one number across all of them. It is
 * not a fallback for "no ledger selected"; someone with access to exactly one
 * ledger simply never sees the switcher.
 *
 * The choice is remembered in localStorage rather than the URL: it is a
 * property of the person using the app, not of the page they are on, and it
 * should survive a refresh without turning every link into a per-ledger link.
 */

const STORAGE_KEY = 'cairn.ledger';

type LedgerContextValue = {
  ledgers: Ledger[];
  /** The selected ledger, or null when viewing everything. */
  current: Ledger | null;
  currentId: string | null;
  select: (ledgerId: string | null) => void;
  /** Ledger ids to send with a request — empty means "no filter". */
  filterIds: string[];
  isLoading: boolean;
  /** Access to the current ledger; the weakest across all when viewing everything. */
  access: Access | null;
  canContribute: boolean;
  canManage: boolean;
  /** Ledgers this person may file receipts into. */
  writable: Ledger[];
};

const LedgerContext = createContext<LedgerContextValue | null>(null);

const RANK: Record<Access, number> = { view: 1, contribute: 2, manage: 3 };

export function LedgerProvider({
  selected,
  onSelect,
  signedIn,
  children,
}: {
  selected: string | null;
  onSelect: (id: string | null) => void;
  signedIn: boolean;
  children: ReactNode;
}) {
  const query = useLedgers({ enabled: signedIn });
  const ledgers = useMemo(() => query.data?.ledgers ?? [], [query.data]);

  // A remembered ledger that has since been archived, or whose access was
  // revoked, must not leave the app pointed at nothing.
  const currentId = selected && ledgers.some((l) => l.id === selected) ? selected : null;
  const current = ledgers.find((l) => l.id === currentId) ?? null;

  const select = useCallback(
    (id: string | null) => {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
      onSelect(id);
    },
    [onSelect],
  );

  const value = useMemo<LedgerContextValue>(() => {
    // Across several ledgers, capability is the weakest one held — offering an
    // action that will fail on some of them is worse than not offering it.
    const access = current
      ? current.access
      : ledgers.length > 0
        ? ledgers.reduce<Access>(
            (weakest, ledger) => (RANK[ledger.access] < RANK[weakest] ? ledger.access : weakest),
            'manage',
          )
        : null;

    return {
      ledgers,
      current,
      currentId,
      select,
      filterIds: currentId ? [currentId] : [],
      isLoading: signedIn && query.isLoading,
      access,
      canContribute: Boolean(access && RANK[access] >= RANK.contribute),
      canManage: Boolean(access && RANK[access] >= RANK.manage),
      writable: ledgers.filter((ledger) => RANK[ledger.access] >= RANK.contribute),
    };
  }, [ledgers, current, currentId, select, signedIn, query.isLoading]);

  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>;
}

export function useLedgerContext(): LedgerContextValue {
  const value = useContext(LedgerContext);
  if (!value) throw new Error('useLedgerContext must be used inside <LedgerProvider>');
  return value;
}

export function readStoredLedger(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
